import { useEffect, useState } from "react";
import { getCommerceReprocessHistory, reprocessConcessionsRollUpTable } from "../services/dimension-service";
import { useGetToken } from "../hooks/custom-hooks";
import toast from "react-hot-toast";
import "../styles/commerce.css";

const formatValue = (value) =>
  value === null || value === undefined ? "—" : value;

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleString([], {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

const ReprocessRows = ({ items, emptyText }) => {
  if (!items.length) {
    return <p className="commerce-reprocess-empty">{emptyText}</p>;
  }

  return (
    <div className="commerce-reprocess-table-wrap">
      <table className="commerce-reprocess-table">
        <thead>
          <tr>
            <th>Changed Item</th>
            <th>Previous Value</th>
            <th>New Value</th>
            <th>Created</th>
            <th>Processed</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.rowToUpdateID}>
              <td>{item.changedItem}</td>
              <td>{formatValue(item.previousDimValue)}</td>
              <td>{formatValue(item.newDimValue)}</td>
              <td>{formatDate(item.createDate)}</td>
              <td>{formatDate(item.processedDate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const CommerceReprocessModal = ({ isOpen, onClose, onReprocessStarted }) => {
  const token = useGetToken();
  const [history, setHistory] = useState({ pendingItems: [], lastProcessedItems: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !token) {
      return;
    }

    let isCurrent = true;

    const loadHistory = async () => {
      setIsLoading(true);
      const response = await getCommerceReprocessHistory(token);

      if (!isCurrent) {
        return;
      }

      if (response.error) {
        toast.error("Unable to load Commerce reprocess details.");
      }

      setHistory(response.data ?? { pendingItems: [], lastProcessedItems: [] });
      setIsLoading(false);
    };

    loadHistory();

    return () => {
      isCurrent = false;
    };
  }, [isOpen, token]);

  if (!isOpen) {
    return null;
  }

  const pendingItems = history.pendingItems ?? [];
  const lastProcessedItems = history.lastProcessedItems ?? [];

  const handleReprocess = async () => {
    setIsSubmitting(true);
    const response = await reprocessConcessionsRollUpTable(token);
    setIsSubmitting(false);

    if (response.data) {
      toast.success("Commerce reprocessing has been started.");
      onReprocessStarted?.();
      onClose();
      return;
    }

    toast.error("Failed to start reprocessing.");
  };

  return (
    <div className="commerce-reprocess-overlay" role="presentation">
      <section className="commerce-reprocess-modal" role="dialog" aria-modal="true" aria-labelledby="commerce-reprocess-title">
        <button className="commerce-reprocess-close" type="button" onClick={onClose} aria-label="Close reprocess details">
          ×
        </button>

        <header className="commerce-reprocess-header">
          <h2 id="commerce-reprocess-title">Commerce Reprocess</h2>
          <p>Review queued dimension changes and the most recently completed reprocess batch.</p>
        </header>

        <div className="commerce-reprocess-content">
          <section className="commerce-reprocess-section">
            <div className="commerce-reprocess-section-heading">
              <div>
                <h3>Queued Changes</h3>
                <p>These changes will be included in the next reprocess.</p>
              </div>
              <span className="commerce-reprocess-count">{pendingItems.length}</span>
            </div>
            {isLoading ? <p className="commerce-reprocess-empty">Loading queued changes…</p> : <ReprocessRows items={pendingItems} emptyText="No changes are waiting to be reprocessed." />}
          </section>

          <section className="commerce-reprocess-section">
            <div className="commerce-reprocess-section-heading">
              <div>
                <h3>Last Reprocessed</h3>
                <p>The most recently completed batch.</p>
              </div>
              <span className="commerce-reprocess-count muted">{lastProcessedItems.length}</span>
            </div>
            {isLoading ? <p className="commerce-reprocess-empty">Loading reprocess history…</p> : <ReprocessRows items={lastProcessedItems} emptyText="No completed reprocess batch is available." />}
          </section>
        </div>

        <footer className="commerce-reprocess-actions">
          <button type="button" className="dim-btn-secondary" onClick={onClose}>Close</button>
          <button type="button" className="dim-btn-primary" onClick={handleReprocess} disabled={!pendingItems.length || isSubmitting}>
            {isSubmitting ? "Starting…" : `Reprocess ${pendingItems.length} Change${pendingItems.length === 1 ? "" : "s"}`}
          </button>
        </footer>
      </section>
    </div>
  );
};
